import { useTranslation } from 'react-i18next'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { DEFAULT_CURRENCY, STOCK_CONDITIONS, type CreateLotInput, type InventoryLocation, type InventoryLot, type StockCondition } from '@jupi/shared'

// The form fields of one stock lot, as strings the way inputs hold them.
// Used on its own (add / edit stock) and as the "first lot" part of adding
// an item.
export interface LotDraft {
  locationId: string
  quantity: string
  serialNumber: string
  batchNumber: string
  condition: StockCondition
  purchasedOn: string
  supplier: string
  reference: string
  unitPrice: string
  currency: string
  expiresOn: string
  notes: string
}

export const EMPTY_LOT: LotDraft = {
  locationId: '',
  quantity: '1',
  serialNumber: '',
  batchNumber: '',
  condition: 'new',
  purchasedOn: '',
  supplier: '',
  reference: '',
  unitPrice: '',
  currency: DEFAULT_CURRENCY,
  expiresOn: '',
  notes: '',
}

export function lotToDraft(lot: InventoryLot): LotDraft {
  return {
    locationId: lot.locationId ?? '',
    quantity: String(lot.quantity),
    serialNumber: lot.serialNumber ?? '',
    batchNumber: lot.batchNumber ?? '',
    condition: lot.condition,
    purchasedOn: lot.purchasedOn ?? '',
    supplier: lot.supplier,
    reference: lot.reference,
    unitPrice: lot.unitPrice === null ? '' : String(lot.unitPrice),
    currency: lot.currency,
    expiresOn: lot.expiresOn ?? '',
    notes: lot.notes,
  }
}

export function draftToLot(draft: LotDraft): CreateLotInput {
  const price = draft.unitPrice.trim()
  return {
    locationId: draft.locationId || null,
    quantity: Number(draft.quantity || 0),
    serialNumber: draft.serialNumber.trim() || null,
    batchNumber: draft.batchNumber.trim() || null,
    condition: draft.condition,
    purchasedOn: draft.purchasedOn || null,
    supplier: draft.supplier.trim(),
    reference: draft.reference.trim(),
    unitPrice: price === '' ? null : Number(price),
    currency: draft.currency.trim().toUpperCase(),
    expiresOn: draft.expiresOn || null,
    notes: draft.notes.trim(),
  }
}

// null when the draft can be saved, else the translation key of what is wrong.
export function lotProblem(draft: LotDraft): string | null {
  const quantity = Number(draft.quantity)
  if (draft.quantity.trim() === '' || !Number.isInteger(quantity) || quantity < 0) return 'inventory.quantity'
  if (draft.serialNumber.trim() && quantity > 1) return 'inventory.serialOnePiece'
  const price = draft.unitPrice.trim()
  if (price !== '' && !(Number(price) >= 0)) return 'inventory.unitPrice'
  if (!/^[A-Za-z]{3}$/.test(draft.currency.trim())) return 'inventory.currency'
  return null
}

export function LotFields({
  draft,
  onChange,
  locations,
  showSerial,
}: {
  draft: LotDraft
  onChange: (draft: LotDraft) => void
  locations: InventoryLocation[]
  // Serial and batch numbers are for aircraft parts.
  showSerial: boolean
}) {
  const { t } = useTranslation()
  const set = (key: keyof LotDraft) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...draft, [key]: e.target.value })
  const date = { slotProps: { inputLabel: { shrink: true } }, type: 'date' as const }
  const serialError = !!draft.serialNumber.trim() && Number(draft.quantity) > 1

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label={t('inventory.quantity')}
          type="number"
          value={draft.quantity}
          onChange={set('quantity')}
          slotProps={{ htmlInput: { min: 0, step: 1, inputMode: 'numeric' } }}
          error={serialError}
          helperText={serialError ? t('inventory.serialOnePiece') : undefined}
          sx={{ flex: 1 }}
        />
        <TextField select label={t('inventory.condition')} value={draft.condition} onChange={set('condition')} sx={{ flex: 1 }}>
          {STOCK_CONDITIONS.map((c) => (
            <MenuItem key={c} value={c}>
              {t(`inventory.conditions.${c}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label={t('inventory.location')} value={draft.locationId} onChange={set('locationId')} sx={{ flex: 1 }}>
          <MenuItem value="">{t('inventory.noLocation')}</MenuItem>
          {locations.map((l) => (
            <MenuItem key={l.locationId} value={l.locationId}>
              {l.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {showSerial ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label={t('inventory.serialNumber')} value={draft.serialNumber} onChange={set('serialNumber')} sx={{ flex: 1 }} />
          <TextField label={t('inventory.batchNumber')} value={draft.batchNumber} onChange={set('batchNumber')} sx={{ flex: 1 }} />
        </Stack>
      ) : null}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label={t('inventory.purchasedOn')} value={draft.purchasedOn} onChange={set('purchasedOn')} sx={{ flex: 1 }} {...date} />
        <TextField label={t('inventory.expiresOn')} value={draft.expiresOn} onChange={set('expiresOn')} sx={{ flex: 1 }} {...date} />
      </Stack>
      <Stack direction="row" spacing={2}>
        <TextField
          label={t('inventory.unitPrice')}
          type="number"
          value={draft.unitPrice}
          onChange={set('unitPrice')}
          slotProps={{ htmlInput: { min: 0, step: '0.01', inputMode: 'decimal' } }}
          sx={{ flex: 2 }}
        />
        <TextField
          label={t('inventory.currency')}
          value={draft.currency}
          onChange={set('currency')}
          slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
          sx={{ flex: 1 }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField label={t('inventory.supplier')} value={draft.supplier} onChange={set('supplier')} sx={{ flex: 1 }} />
        <TextField label={t('inventory.reference')} value={draft.reference} onChange={set('reference')} sx={{ flex: 1 }} />
      </Stack>
      <TextField label={t('inventory.notes')} value={draft.notes} onChange={set('notes')} multiline minRows={2} />
    </Stack>
  )
}
